import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { catchError, map, Observable, of, retry, switchMap, tap, timer } from 'rxjs';

import { AuthService } from '@ideals/core/auth';
import { Culture } from '@ideals/core/localization';
import { LoggerService } from '@ideals/core/logger';
import { UploadEvent, UploadService } from '@ideals/services/upload';
import { createHttpBody } from '@ideals/utils/create-http-body';
import { createHttpParams } from '@ideals/utils/create-http-params';
import { sortItems } from '@ideals/utils/sort-items';
import { uniqueId } from '@ideals/utils/unique-id';

import {
  DetailedProject,
  DocumentPermission,
  PremierPlanFeatures,
  ProjectBase,
  ProjectLockDetails,
  ProjectLockReason,
  ProjectRedactionState,
  ProjectStatus,
  ProjectType,
  RequestError,
  SubscriptionPlan,
} from '@vdr/data/models';
import { getProjectsUrl } from '@vdr/data/utils/get-projects-url';

import { environment } from '../../../../environments/environment';
import { BrandingSettings, BrandingSettingsBase, InvitationCustomization } from '../../../branding-settings/models';
import { RetentionPeriod } from '../../../corporate-account/models';
import { ImmediateProjectClosure, ProjectClosingReason, ScheduledProjectClosure } from '../../../project-closure/models';
import {
  ArchiveProjectData,
  CreateProjectData,
  CreateStagingProjectData,
  ListProject,
  ListProjectUsage,
  ProjectServer,
  ProjectServers,
  ProjectSpecialFolders,
  ProjectUseCase,
} from '../../models';

interface ProjectUpdatesDto {
  readonly autoIndexEnabled: boolean;
  readonly endingCode: string;
  readonly projectLinkWithoutEndingCode: string;
  readonly projectName: string;
  readonly supportContacts: string;
}

const SECOND = 1000;
const ACCESS_CHECK_RETRY_COUNT = 15;

class NeedRetryError extends Error {}

@Service()
export class ProjectsService {
  readonly #authService = inject(AuthService);
  readonly #httpClient = inject(HttpClient);
  readonly #loggerService = inject(LoggerService);
  readonly #uploadService = inject(UploadService);

  archiveProject(project: DetailedProject, data: ArchiveProjectData): Observable<ProjectLockDetails> {
    const body = {
      duration: {
        period: 0,
        customPeriodUnit: 2,
        customPeriodLength: data.duration,
      },
      accessRights: {
        projectUserEmails: data.participants,
        externalUserEmails: data.externalParticipants,
      },
      isFreePeriodUsed: data.freePeriodUsed,
    };

    return this.#httpClient.put<void>(`/api/ca/rooms/${project.id}/archiving`, body).pipe(
      map(() => ({
        lockReasons: ['NotCompletedRoomArchivingPreparation'],
        usersWithUnlockRights: [],
      })),
    );
  }

  cancelScheduledProjectClosure(project: DetailedProject): Observable<void> {
    return this.#httpClient.delete<void>(`/api/ca/rooms/${project.id}/closure/schedule`);
  }

  checkIfNeedToSetUseCase(project: DetailedProject): Observable<boolean> {
    return this.#httpClient.get<boolean>(`/api/ca/use-case/${project.id}/should-show`)
      .pipe(catchError(() => of(false)));
  }

  closeProject(project: DetailedProject, closure?: ImmediateProjectClosure): Observable<void> {
    const body = closure
      ? {
        clientSatisfactionComment: closure.satisfactionSurveyResult?.feedback,
        clientSatisfactionRate: closure.satisfactionSurveyResult?.rate,
        closingReason: closure.reason,
        closingReasonComment: closure.comment,
        retentionSettings: closure.reduceRetentionPeriod
          ? {
            period: RetentionPeriod.Days90,
            customPeriodLength: null,
            customPeriodUnit: null,
          }
          : null,
      }
      : {};

    return this.#httpClient.put<void>(`/api/ca/rooms/${project.id}/closure`, body);
  }

  createProject(data: CreateProjectData): Observable<ListProject> {
    const body = {
      attributes: data.attributes,
      accountId: data.accountId,
      accountName: data.accountName,
      administrators: data.administrators,
      hostId: data.hostId,
      numberOfProjects: data.numberOfProjects,
      roomName: data.name,
      serviceTerm: data.serviceTerm,
      storage: data.storage,
      useCase: data.typeIds[0],
    };

    return this.#httpClient.post<{ readonly roomId: string; }>('/api/ca/rooms/v2', body).pipe(
      switchMap(({ roomId }) => this.#checkCreationStatus(roomId)),
      switchMap((roomId) => this.#loadProjectFromCA(roomId)),
      map((project) => ({
        ...project,
        isFavorite: false,
      })),
    );
  }

  createStagingProject(stagingProject: CreateStagingProjectData): Observable<ListProject> {
    const body = {
      ...stagingProject,
      administrators: stagingProject.administrators.map(({ email }) => email),
      roomName: stagingProject.name,
    };

    return this.#httpClient.post<{ readonly roomId: string; }>('/api/ca/rooms/v2', body).pipe(
      switchMap(({ roomId }) => this.#checkCreationStatus(roomId)),
      switchMap((roomId) => this.#loadProjectFromCA(roomId)),
      map((project) => ({
        ...project,
        isFavorite: false,
      })),
    );
  }

  createTrialProject(sourceProject: ProjectBase, name: string): Observable<ListProject> {
    return this.#httpClient.post<{
      readonly roomId: string;
      readonly status: number;
    }>(`/api/ca/rooms/v2/${sourceProject.id}/staging`, { roomName: name })
      .pipe(
        switchMap(({ roomId }) => this.#checkCreationStatus(roomId)),
        switchMap((roomId) => this.#loadProjectFromCA(roomId)),
        map((project) => ({
          ...project,
          isFavorite: false,
        })),
      );
  }

  deleteProjectLoginPageBackground(project: DetailedProject): Observable<void> {
    return this.#httpClient.delete<void>(getProjectsUrl(project.hostId, `/${project.id}/general-settings/login-background`));
  }

  deleteProjectLogo(project: DetailedProject): Observable<void> {
    return this.#httpClient.delete<void>(getProjectsUrl(project.hostId, `/${project.id}/general-settings/logo`));
  }

  loadAdministratedProjects(
    statuses: ProjectStatus[] = ['Active', 'Archived', 'Closed', 'Frozen', 'Locked', 'Preparation', 'Suspended'], // projects in all statuses are loaded by default
  ): Observable<ListProject[]> {
    const params = createHttpParams({ statuses });

    return this.#httpClient.get<{
      readonly displayProjectName: boolean;
      readonly hostNumber: number;
      readonly id: string;
      readonly lastAccessedOn: string;
      readonly logoUrl: string;
      readonly logoUrlV3: string;
      readonly mainColor: string;
      readonly name: string;
      readonly roomPlan: SubscriptionPlan;
      readonly status: ProjectStatus;
      readonly title: string;
      readonly type: ProjectType;
    }[]>('/api/ca/rooms/administrated', { params }).pipe(
      map((projects) => projects.map((project): ListProject => ({
        accountId: 'Unknown',
        createdOn: new Date(0),
        displayName: project.displayProjectName,
        hostId: project.hostNumber.toString(),
        id: project.id,
        isFavorite: false,
        lastAccessedOn: project.lastAccessedOn ? new Date(project.lastAccessedOn) : undefined,
        logoUrl: project.logoUrlV3 ?? project.logoUrl ?? undefined,
        mainColor: project.mainColor,
        name: project.name,
        status: project.status,
        subscriptionPlan: project.roomPlan,
        title: project.title,
        type: project.type,
      }))),
      map((projects) => this.#sortProjects(projects)),
    );
  }

  loadBrandingSettings(hostId: string, name: string): Observable<BrandingSettings> {
    const url = getProjectsUrl(hostId, `/${name}/branding-settings`);

    return this.#httpClient.get<{
      readonly invitedByUserEmail: string;
      readonly invitedByUserName: string;
      readonly loginBackgroundEnabled: boolean;
      readonly loginBackgroundUrl: string;
      readonly logoUrl: string;
      readonly mainColor: string;
    }>(url).pipe(
      map((dto) => ({
        ...dto,
        loginBackgroundUrl: dto.loginBackgroundUrl || undefined,
        logoUrl: dto.logoUrl || undefined,
      })),
    );
  }

  loadLockDetails(project: ProjectBase): Observable<ProjectLockDetails> {
    const url = getProjectsUrl(project.hostId, `/${project.id}/locking`);

    return this.#httpClient.get<{
      certificateId?: string;
      reasonTypes: ProjectLockReason[];
      usersWithUnlockingRights: number[];
    }>(url).pipe(
      map((details) => ({
        certificateId: details.certificateId,
        lockReasons: details.reasonTypes,
        usersWithUnlockRights: details.usersWithUnlockingRights,
      })),
    );
  }

  loadProject(hostId: string, nameOrId: string): Observable<DetailedProject> {
    return this.#loadProjectDto(hostId, nameOrId).pipe(
      switchMap((projectDto) => this.loadSpecialFolders(hostId, projectDto.id).pipe(
        map((specialFolders): DetailedProject => ({
          accountId: projectDto.accountId,
          allowedEmailDomains: projectDto.allowedEmailDomains?.split(','),
          autoIndexSupported: projectDto.autoIndexSupported ?? true,
          autoIndexEnabled: projectDto.autoIndexEnabled,
          brandingEnabled: projectDto.brandingEnabled,
          closingDate: projectDto.closingDate ? new Date(projectDto.closingDate) : null,
          createdOn: new Date(projectDto.createdOn),
          displayName: projectDto.displayProjectName,
          documentPublishingSupported: projectDto.documentPublishingSupported,
          documentsPublishingEnabled: projectDto.documentsPublishingEnabled,
          documentsVersioningEnabled: projectDto.documentsVersioningEnabled,
          hostId,
          id: projectDto.id,
          isTwoFactorAuthMandatory: projectDto.isTwoFactorAuthMandatory,
          labelsEnabled: projectDto.labelsEnabled,
          linkEndingCode: projectDto.endingCode ? `_${projectDto.endingCode}` : '',
          loginBackgroundEnabled: projectDto.loginBackgroundEnabled,
          loginBackgroundUrl: projectDto.loginBackgroundUrl || undefined,
          logoUrl: projectDto.logoUrl ?? undefined,
          mainColor: projectDto.mainColor,
          name: projectDto.name,
          qnaApproversAllowed: projectDto.qnaApproversAllowed,
          qnaStatus: projectDto.qnaStatus,
          redactionEnabled: projectDto.redactionEnabled,
          serverName: projectDto.serverName,
          specialFolders,
          status: projectDto.status,
          subscriptionPlan: projectDto.plan,
          supportContacts: projectDto.supportContacts ?? '',
          termsOfUseEnabled: projectDto.termsOfUseEnabled,
          title: projectDto.title,
          type: projectDto.type,
          uiId: projectDto.uiId,
          useCaseId: projectDto.useCaseId,
          useNewActivityLogService: projectDto.useNewActivityLogService,
          useNewFileStorage: projectDto.useNewFileStorage,
          watermarksEnabled: projectDto.watermarksEnabled,
        })),
      )),
    );
  }

  loadProjectByUiId(hostId: string, uiId: string): Observable<ProjectBase> {
    const params = createHttpParams({ hostIdAndRoomId: `${hostId}_${uiId}` });

    return this.#httpClient.get<{
      displayProjectName: boolean;
      hasSupportAccess: boolean;
      hostNumber: number;
      id: string;
      logoUrlV3: string;
      mainColor: string;
      name: string;
      status: ProjectStatus;
      title: string;
      type: ProjectType;
    }>('/api/ca/rooms', { params }).pipe(
      map((dto): ProjectBase => ({
        accountId: 'Unknown',
        createdOn: new Date(0),
        displayName: dto.displayProjectName,
        hasSupportAccess: dto.hasSupportAccess,
        hostId: `${dto.hostNumber}`,
        id: dto.id,
        logoUrl: dto.logoUrlV3 || undefined,
        mainColor: dto.mainColor,
        name: dto.name,
        status: dto.status,
        title: dto.title,
        type: dto.type,
      })),
    );
  }

  loadProjectRedactionState(project: DetailedProject): Observable<ProjectRedactionState> {
    return this.#httpClient.get<ProjectRedactionState>(getProjectsUrl(project.hostId, `/${project.id}/metadata/redaction`));
  }

  loadProjects(): Observable<ListProject[]> {
    return this.#httpClient.get<{
      readonly accountId: string;
      readonly accountManager: string | null;
      readonly businessDevelopmentManager: string | null;
      readonly createdOn: string;
      readonly displayName: boolean;
      readonly hostId: string;
      readonly id: string;
      readonly lastAccessedOn: string;
      readonly logoUrl: string;
      readonly mainColor: string;
      readonly name: string;
      readonly roomPlan: SubscriptionPlan;
      readonly status: ProjectStatus;
      readonly title: string;
      readonly type: ProjectType;
    }[]>('/api/ca/projects').pipe(
      map((projects) => projects.map((project): ListProject => ({
        accountId: project.accountId,
        accountManager: project.accountManager ?? undefined,
        businessDevelopmentManager: project.businessDevelopmentManager ?? undefined,
        createdOn: new Date(project.createdOn),
        displayName: project.displayName,
        hostId: project.hostId,
        id: project.id,
        isFavorite: false,
        lastAccessedOn: project.lastAccessedOn ? new Date(project.lastAccessedOn) : undefined,
        logoUrl: project.logoUrl ?? undefined,
        mainColor: project.mainColor,
        name: project.name,
        status: project.status,
        subscriptionPlan: project.roomPlan,
        title: project.title,
        type: project.type,
      }))),
      map((projects) => this.#sortProjects(projects)),
    );
  }

  loadProjectsBrandingSettings(projectIds: string[]): Observable<Record<string, BrandingSettingsBase>> {
    const url = '/api/ca/branding-settings/rooms';

    return this.#httpClient.post<{
      readonly color: {
        readonly main: string;
      };
      readonly logoV3: {
        readonly location: string | null;
      };
      readonly roomId: string;
      readonly welcome: {
        readonly location: string | null;
      };
    }[]>(
      url,
      { roomIds: projectIds },
    ).pipe(
      map((dtos) => {
        const entries = dtos.map((dto) => {
          const settings: BrandingSettingsBase = {
            loginBackgroundUrl: dto.welcome.location ?? undefined,
            logoUrl: dto.logoV3.location ?? undefined,
            mainColor: dto.color.main,
          };

          return [dto.roomId, settings] as [string, BrandingSettingsBase];
        });

        return Object.fromEntries(entries);
      }),
    );
  }

  loadProjectServers(culture: Culture, accountIds: string[] = []): Observable<ProjectServers> {
    const body = {
      accountIds,
      culture,
      continentName: this.#authService.geolocation().continentName,
      countryName: this.#authService.geolocation().countryName,
    };

    return this.#httpClient.post<{
      readonly accountsToAvailableServersMap: {
        readonly accountId: string;
        readonly servers: ProjectServer[];
      }[];
      readonly allServers: ProjectServer[];
    }>(
      '/api/ca/servers',
      body,
    ).pipe(
      map(({ allServers, accountsToAvailableServersMap }) => {
        const servers: ProjectServers = {
          all: allServers,
          ...accountsToAvailableServersMap.reduce<Record<string, ProjectServer[]>>((acc, value) => {
            acc[value.accountId] = value.servers;

            return acc;
          }, {}),
        };

        return servers;
      }),
    );
  }

  loadProjectStatus(hostId: string, nameOrId: string): Observable<ProjectStatus> {
    return this.#loadProjectDto(hostId, nameOrId).pipe(
      map((projectDto) => projectDto.status),
    );
  }

  loadProjectsUsage(roomIds: string[]): Observable<Record<string, ListProjectUsage>> {
    return this.#httpClient.post<{
      readonly data: number;
      readonly participantsCount: number;
      readonly roomId: string;
    }[]>(
      '/api/usage/rooms/usage',
      roomIds,
    ).pipe(
      map((usages) => usages.reduce((acc, project) => {
        acc[project.roomId] = { id: project.roomId, usedStorage: project.data, usersCount: project.participantsCount };

        return acc;
      }, {} as Record<string, ListProjectUsage>)),
    );
  }

  loadSatisfactionSurveyVisibility(project: DetailedProject): Observable<boolean> {
    return this.#httpClient.get<{ display: boolean; }>(getProjectsUrl(project.hostId, `/${project.id}/user/survey`)).pipe(
      map(({ display }) => display),
    );
  }

  loadScheduledProjectClosure(project: DetailedProject): Observable<ScheduledProjectClosure | undefined> {
    interface Dto {
      readonly closingDate: string | null;
      readonly closingReason: ProjectClosingReason | null;
      readonly closingReasonComment: string | null;
    }

    return this.#httpClient.get<Dto>(`/api/ca/rooms/${project.id}/closure/schedule`).pipe(
      map((dto) => {
        return dto.closingDate
          ? {
            comment: dto.closingReasonComment ?? undefined,
            date: new Date(dto.closingDate),
            reason: dto.closingReason!,
            satisfactionSurveyResult: undefined,
          }
          : undefined;
      }),
    );
  }

  loadSpecialFolders(hostId: string, projectId: string): Observable<ProjectSpecialFolders> {
    return this.#httpClient.get<{
      readonly qaFolder: {
        readonly id: string;
        readonly permission: DocumentPermission;
      };
      readonly recycleBin: {
        readonly id: string;
        readonly permission: DocumentPermission;
      };
      readonly root: {
        readonly id: string;
      };
      readonly sandboxFolder?: {
        readonly id: string;
        readonly permission: DocumentPermission;
      };
    }>(getProjectsUrl(hostId, `/${projectId}/documents/special-folders`)).pipe(
      map((dto) => ({
        qnaAttachmentsId: dto.qaFolder.id,
        qnaAttachmentsPermission: dto.qaFolder.permission,
        recycleBinId: dto.recycleBin.id,
        recycleBinPermission: dto.recycleBin.permission,
        rootId: dto.root.id,
        ...dto.sandboxFolder
          ? {
            sandboxId: dto.sandboxFolder.id,
            sandboxPermissions: dto.sandboxFolder.permission,
          }
          : {},
      })),
    );
  }

  loadUseCase(project: DetailedProject): Observable<number | undefined> {
    return this.#httpClient.get<{ useCase: number; }>(`/api/ca/use-case/${project.id}`)
      .pipe(
        map(({ useCase }) => useCase),
        catchError(() => of(undefined)),
      );
  }

  loadUseCases(): Observable<ProjectUseCase[]> {
    return this.#httpClient.get<ProjectUseCase[]>(getProjectsUrl(environment.projectProviderUrlDefaultId, '/use-cases')).pipe(
      map((useCases) => this.#sortUseCases(useCases)),
    );
  }

  lockProject(project: ProjectBase, administratorIds: number[] | null): Observable<ProjectLockDetails> {
    const url = getProjectsUrl(project.hostId, `/${project.id}/locking`);

    return this.#httpClient.put(url, { usersWithUnlockingRights: administratorIds }).pipe(
      map(() => ({
        lockReasons: [],
        usersWithUnlockRights: administratorIds ?? [],
      })),
    );
  }

  requestSubscriptionPlanUpgrade(project: DetailedProject, feature: PremierPlanFeatures): Observable<void> {
    return this.#httpClient.post<void>(getProjectsUrl(project.hostId, `/${project.id}/general-settings/upgrade-request`), {
      upgradeRequestOptions: feature,
    });
  }

  scheduleProjectClosure(project: DetailedProject, closure: ScheduledProjectClosure): Observable<void> {
    const body = {
      closingDate: closure.date,
      clientSatisfactionComment: closure.satisfactionSurveyResult?.feedback,
      clientSatisfactionRate: closure.satisfactionSurveyResult?.rate,
      closingReason: closure.reason,
      closingReasonComment: closure.comment,
      retentionSettings: closure.reduceRetentionPeriod
        ? {
          period: RetentionPeriod.Days90,
          customPeriodLength: null,
          customPeriodUnit: null,
        }
        : null,
    };

    return this.#httpClient.put<void>(`/api/ca/rooms/${project.id}/closure`, body);
  }

  snoozeProjectUseCaseReminder(project: DetailedProject): Observable<void> {
    return this.#httpClient.put<void>(`/api/ca/use-case/${project.id}/snooze`, null);
  }

  unlockProject(project: ProjectBase): Observable<void> {
    const url = getProjectsUrl(project.hostId, `/${project.id}/unlocking`);

    return this.#httpClient.put<void>(url, {});
  }

  updateColorTheme(project: DetailedProject, hoverColor: string, mainColor: string): Observable<void> {
    return this.#httpClient.put<void>(getProjectsUrl(project.hostId, `/${project.id}/general-settings/color-theme`), {
      // hoverColor is needed for vdr3
      hoverColor,
      mainColor,
    });
  }

  updateInvitationCustomization(project: DetailedProject, invitationCustomization: InvitationCustomization): Observable<void> {
    return this.#httpClient.put<void>(
      getProjectsUrl(project.hostId, `/${project.id}/general-settings/invitation-customization`),
      createHttpBody(invitationCustomization),
    );
  }

  updateProject(project: DetailedProject): Observable<DetailedProject> {
    return this.#httpClient.put<ProjectUpdatesDto>(getProjectsUrl(project.hostId, `/${project.id}/general-settings`), {
      autoIndexEnabled: project.autoIndexEnabled,
      documentsPublishingEnabled: project.documentsPublishingEnabled,
      documentsVersioningEnabled: project.documentsVersioningEnabled,
      projectLinkWithoutEndingCode: project.name.slice(0, project.name.lastIndexOf(project.linkEndingCode)),
      projectName: project.title,
      supportContacts: project.supportContacts,
    }).pipe(
      map(({ autoIndexEnabled, endingCode, projectLinkWithoutEndingCode, projectName, supportContacts }) => ({
        ...project,
        autoIndexEnabled,
        supportContacts,
        linkEndingCode: `_${endingCode}`,
        name: `${projectLinkWithoutEndingCode}_${endingCode}`,
        title: projectName,
      })),
    );
  }

  updateProjectStatus(project: DetailedProject, status: 'Active' | 'Preparation'): Observable<void> {
    return this.#httpClient.put<void>(`/api/ca/rooms/${project.id}/status`, { status });
  }

  updateProjectUseCase(project: DetailedProject, useCaseId: number): Observable<void> {
    return this.#httpClient.put<void>(`/api/ca/use-case/${project.id}`, { useCase: useCaseId });
  }

  uploadProjectLoginPageBackground(project: DetailedProject, file: File): Observable<UploadEvent> {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('id', uniqueId());
    formData.append('contentLength', file.size.toString());

    return this.#uploadService.uploadFile({
      formData,
      url: getProjectsUrl(project.hostId, `/${project.id}/general-settings/login-background`),
    });
  }

  uploadProjectLogo(project: DetailedProject, file: File): Observable<UploadEvent> {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('id', uniqueId());
    formData.append('contentLength', file.size.toString());

    return this.#uploadService.uploadFile({
      formData,
      url: getProjectsUrl(project.hostId, `/${project.id}/general-settings/logo`),
    });
  }

  #checkCreationStatus(projectId: string, retries = ACCESS_CHECK_RETRY_COUNT): Observable<string> {
    const needRetryError = new NeedRetryError('Creation of project in progress');

    return this.#httpClient.get<{
      readonly roomId: string;
      readonly status: 'Pending' | 'Succeeded' | 'Failed';
    }>(`/api/ca/rooms/${projectId}/creation-status`)
      .pipe(
        tap({
          next: ({ status }) => {
            if (status === 'Pending') {
              throw needRetryError;
            }
          },
          error: (error: RequestError) => {
            if (error.status === 'Not Found') {
              throw needRetryError;
            }

            throw error;
          },
        }),
        retry({
          count: retries,
          delay: (error: Error | NeedRetryError, retryCount) => {
            if (error instanceof NeedRetryError) {
              return timer(retryCount * SECOND);
            }

            throw error;
          },
        }),
        catchError((error: Error | NeedRetryError) => {
          if (error instanceof NeedRetryError) {
            this.#loggerService.handleJsError(
              new Error(`Retries is stopped for /api/ca/rooms/${projectId}/creation-status. Count of retries: ${retries}`),
              'warn',
            );
          }

          throw error;
        }),
        map(() => projectId),
      );
  }

  #loadProjectDto<DTO = {
    readonly accountId: string;
    readonly allowedEmailDomains: string;
    readonly autoIndexEnabled: boolean;
    readonly autoIndexSupported: boolean;
    readonly brandingEnabled: boolean;
    readonly closingDate: string | null;
    readonly createdOn: string;
    readonly displayProjectName: boolean;
    readonly documentPublishingSupported: boolean;
    readonly documentsPublishingEnabled: boolean;
    readonly documentsVersioningEnabled: boolean;
    readonly endingCode: string;
    readonly id: string;
    readonly isTwoFactorAuthMandatory: boolean;
    readonly labelsEnabled: boolean;
    readonly loginBackgroundEnabled: boolean;
    readonly loginBackgroundUrl: string;
    readonly logoUrl?: string;
    readonly mainColor: string;
    readonly name: string;
    readonly plan: SubscriptionPlan;
    readonly qnaApproversAllowed: boolean;
    readonly qnaStatus: 'Activated' | 'Disabled' | 'Enabled';
    readonly redactionEnabled: boolean;
    readonly serverName: string;
    readonly status: ProjectStatus;
    readonly supportContacts: string | null;
    readonly termsOfUseEnabled: boolean;
    readonly title: string;
    readonly type: ProjectType;
    readonly uiId: string;
    readonly useCaseId?: number;
    readonly useNewActivityLogService: boolean;
    readonly useNewFileStorage: boolean;
    readonly watermarksEnabled: boolean;
  }>(hostId: string, nameOrId: string): Observable<DTO> {
    return this.#httpClient.get<DTO>(getProjectsUrl(hostId, `/${nameOrId}`));
  }

  #loadProjectFromCA(projectId: string): Observable<ProjectBase & { readonly subscriptionPlan: SubscriptionPlan; }> {
    return this.#httpClient.get<{
      readonly accountId: string;
      readonly createdOn: string;
      readonly hostId: number;
      readonly id: string;
      readonly mainColor: string;
      readonly projectType: ProjectType;
      readonly roomName: string;
      readonly roomPlan: SubscriptionPlan;
      readonly title: string;
    }>(`/api/ca/rooms/${projectId}`)
      .pipe(
        map((dto) => ({
          accountId: dto.accountId,
          createdOn: new Date(dto.createdOn),
          displayName: true,
          hostId: dto.hostId.toString(),
          id: dto.id,
          mainColor: dto.mainColor,
          name: dto.roomName,
          status: 'Active' as const,
          subscriptionPlan: dto.roomPlan,
          title: dto.title,
          type: dto.projectType,
        })),
      );
  }

  #sortProjects<T extends ProjectBase>(projects: T[]): T[] {
    return sortItems(
      projects,
      [
        { field: 'lastAccessedOn', order: -1 },
        { field: 'name', order: 1 },
      ],
      {
        lastAccessedOn: (value) => value ?? new Date(0),
      },
    );
  }

  #sortUseCases(useCases: ProjectUseCase[]): ProjectUseCase[] {
    const sorted = sortItems(
      useCases,
      [
        { field: 'position', order: 1 },
      ],
    );

    return sorted.map((useCase) => {
      return useCase.useCases
        ? { ...useCase, useCases: this.#sortUseCases(useCase.useCases) }
        : useCase;
    });
  }
}
